import { apiRequest } from "src/async/apiUtils";

const tasks = new Map();
const listeners = new Map();

const notify = (key) => {
  const subs = listeners.get(key);
  if (!subs) return;
  const snap = tasks.get(key);
  for (const fn of subs) fn({ ...snap });
};

const ensureTask = (key) => {
  if (!tasks.has(key)) {
    tasks.set(key, {
      status: "idle",
      progress: 0,
      hasProgress: false,
      headers: null,
      name: null,
      extension: null,
      mime: null,
      blob: null,
      url: null,
      error: null,
      startedAt: null,
      endedAt: null,
      events: [],
      controller: null,
      ttlUntil: null,
    });
  }
  return tasks.get(key);
};

const extractNameAndExt = (headers, fallbackName = "file") => {
  const cd = headers?.["content-disposition"] || headers?.["Content-Disposition"];
  const m = cd?.match(/filename[^;=\n]*=(['"]?)([^'"\n]*)\1?/);
  const name = m?.[2] || fallbackName;
  const extension = (name.split(".").pop() || "").toLowerCase();
  return { name, extension };
};

const decodeAxiosBlobError = async (err) => {
  try {
    if (err?.response?.data instanceof Blob) {
      const text = await err.response.data.text();
      try {
        const j = JSON.parse(text);
        return j?.message || j?.error || text;
      } catch {
        return text || "";
      }
    }
  } catch {}
  return err?.response?.data?.message || err?.message || "Request failed";
};

const now = () => Date.now();

export const makeKey = (base, data = {}) => {
  const entries = Object.entries(data).filter(([, v]) => v != null);
  entries.sort(([a], [b]) => (a > b ? 1 : a < b ? -1 : 0));
  const qs = new URLSearchParams(Object.fromEntries(entries)).toString();
  return qs ? `${base}?${qs}` : String(base);
};

const runWithApiRequest = async (requestCfg, { signal, onDownloadProgress }) => {
  const { method = "GET", url = "", payload = { body: null, params: {}, queries: null }, options = {}, responseProcessor } = requestCfg || {};

  const mergedOptions = {
    ...options,
    is_abort: true,
    signal,
    responseType: options?.responseType || "blob",
    onDownloadProgress,
    returnErrorWithstatus: true,
  };

  const res = await apiRequest(method, url, payload, mergedOptions);
  if (typeof responseProcessor === "function") return responseProcessor(res);
  return { blob: res?.data, headers: res?.headers };
};

export const DownloadManager = {
  subscribe(key, cb) {
    if (!key) throw new Error("subscribe requires a key string");
    ensureTask(key);
    if (!listeners.has(key)) listeners.set(key, new Set());
    listeners.get(key).add(cb);
    cb({ ...tasks.get(key) });
    return () => {
      const set = listeners.get(key);
      if (set) {
        set.delete(cb);
        if (set.size === 0) listeners.delete(key);
      }
    };
  },

  async start(key, config = {}) {
    if (!key) throw new Error("start requires a key string");
    const task = ensureTask(key);
    const { runner, request, cache, createObjectURL, force = false } = config;
    if (!force && task.status === "done" && task.ttlUntil && now() <= task.ttlUntil) {
      return task;
    }
    if (task.status === "downloading") return task;
    const ttlMs = cache?.ttlMs ?? null;
    try {
      task.controller?.abort();
    } catch {}
    task.controller = new AbortController();
    task.status = "downloading";
    task.error = null;
    task.progress = 0;
    task.hasProgress = false;
    task.startedAt = now();
    task.endedAt = null;
    task.events.push({ t: now(), type: "start" });
    notify(key);
    const setProgress = (pct) => {
      const v = Math.max(0, Math.min(100, Math.round(pct)));
      task.progress = Math.max(task.progress || 0, v);
      task.hasProgress = task.hasProgress || v > 0;
      task.events.push({ t: now(), type: "progress", data: { pct: v } });
      notify(key);
    };

    const onDownloadProgress = (evt) => {
      if (evt && evt.total) {
        const pct = Math.max(1, Math.min(99, Math.round((evt.loaded / evt.total) * 100)));
        setProgress(pct);
      }
    };

    try {
      let result;

      if (typeof runner === "function") {
        result = await runner({ signal: task.controller.signal, onDownloadProgress, setProgress, key });
      } else if (request) {
        result = await runWithApiRequest(request, { signal: task.controller.signal, onDownloadProgress });
      } else {
        throw new Error("No runner or request provided to DownloadManager.start");
      }

      let { blob, headers, name, extension, mime, url } = result || {};
      if (!blob && result?.data instanceof Blob) blob = result.data;

      const shouldCreateUrl = typeof createObjectURL === "boolean" ? createObjectURL : blob instanceof Blob;
      if (!url && shouldCreateUrl && blob instanceof Blob) url = URL.createObjectURL(blob);
      if (!mime && blob instanceof Blob) mime = blob.type;

      if (!name || !extension) {
        const meta = extractNameAndExt(headers, name || "file");
        name = name || meta.name;
        extension = extension || meta.extension;
      }

      if (!blob && !url) throw new Error("No data to preview");

      task.headers = headers || null;
      task.name = name || task.name;
      task.extension = extension || task.extension;
      task.mime = mime || task.mime;
      task.blob = blob || task.blob;
      task.url = url || task.url;

      setProgress(100);
      task.status = "done";
      task.endedAt = now();
      task.ttlUntil = ttlMs ? task.endedAt + ttlMs : null;
      task.events.push({ t: now(), type: "done" });
      task.controller = null;
      notify(key);
      return task;
    } catch (err) {
      if (err?.name === "CanceledError" || err?.code === "ERR_CANCELED" || err?.name === "AbortError" || err?.message === "canceled") {
        task.status = "canceled";
        task.error = null;
        task.endedAt = now();
        task.events.push({ t: now(), type: "canceled" });
        task.controller = null;
        notify(key);
        return task;
      }

      const message = await decodeAxiosBlobError(err);
      task.status = "error";
      task.error = message;
      task.endedAt = now();
      task.events.push({ t: now(), type: "error", data: { message } });
      task.controller = null;
      notify(key);
      return task;
    }
  },

  snapshot(key) {
    if (!key) return null;
    const t = tasks.get(key);
    return t ? { ...t } : null;
  },

  cancel(key) {
    if (!key) return;
    const t = tasks.get(key);
    if (t?.controller) {
      try {
        t.controller.abort();
      } catch {}
    }
  },

  clear(key) {
    if (!key) return;
    const t = tasks.get(key);
    try {
      t?.controller?.abort();
    } catch {}
    if (t?.url && t.url.startsWith("blob:")) URL.revokeObjectURL(t.url);
    tasks.delete(key);
    listeners.delete(key);
  },
};
